// src/views/Trading.js — BESC Hyperchain, powered by MoneyX Indexer REST API
import React, { useEffect, useState, useMemo } from 'react'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  Bar,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts'

import { tsToIso } from '../helpers'

const API_BASE = typeof window === 'undefined'
  ? (process.env.REACT_APP_INDEXER_URL || 'https://api.moneyxpro.com')
  : ''

// BESC synthetic index tokens — addresses match frontend/src/config/tokens.ts
const TOKENS = [
  { symbol: 'BTC',   address: '0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c' },
  { symbol: 'ETH',   address: '0x2170ed0880ac9a755fd29b2688956bd959f933f8' },
  { symbol: 'BNB',   address: '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c' },
  { symbol: 'SOL',   address: '0x570a5d26f7765ecb712c0924e4de545b89fd43df' },
  { symbol: 'XRP',   address: '0x1d2f0da169ceb9fc7b3144628db156f3f6c60dbe' },
  { symbol: 'DOGE',  address: '0xba2ae424d960c26247dd6c32edc70b295c744c43' },
  { symbol: 'ADA',   address: '0x3ee2200efb3400fabb9aacf31297cbdd1d435d47' },
  { symbol: 'TRX',   address: '0x85eac5ac2f758618dfa09bdbe0cf174e7d574d5b' },
  { symbol: 'LINK',  address: '0xf8a0bf9cf54bb92f17374d9e9a321e6a111a51bd' },
  { symbol: 'AVAX',  address: '0x1ce0c2827e2ef14d5c4f29a091d735a204794041' },
  { symbol: 'SHIB',  address: '0x2859e4544c4bb03966803b044a93563bd2d0dd4d' },
  { symbol: 'TON',   address: '0x76a797a59ba2c17726896976b7b3747bfd1d220f' },
  { symbol: 'DOT',   address: '0x7083609fce4d1d8dc0c979aab8c869ea2c873402' },
  { symbol: 'NEAR',  address: '0x1fa4a73a3f0133f0025378af00236f3abdee5d63' },
  { symbol: 'MATIC', address: '0xcc42724c6683b7e57334c4e856f4c9965ed682bd' },
  { symbol: 'LTC',   address: '0x4338665cbb7b2485a8855a139b75d5e34ab0db94' },
  { symbol: 'UNI',   address: '0xbf5140a22578168fd562dccf235e5d43a02ce9b1' },
  { symbol: 'ATOM',  address: '0x0eb3a705fc54725037cc9e008bdede697f62f335' },
  { symbol: 'ARB',   address: '0xa050ffb3eeb8200eeb7f61ce34ff644420fd3522' },
  { symbol: 'OP',    address: '0x5c1e3a76aba3c84afbce7a38e8ef9dea3f9adfc8' },
  { symbol: 'AAPL',  address: '0x53544f434b000000000000000000000000000001' },
  { symbol: 'TSLA',  address: '0x53544f434b000000000000000000000000000002' },
  { symbol: 'NVDA',  address: '0x53544f434b000000000000000000000000000003' },
  { symbol: 'MSFT',  address: '0x53544f434b000000000000000000000000000004' },
  { symbol: 'GOOGL', address: '0x53544f434b000000000000000000000000000005' },
  { symbol: 'AMZN',  address: '0x53544f434b000000000000000000000000000006' },
  { symbol: 'META',  address: '0x53544f434b000000000000000000000000000007' },
  { symbol: 'SPY',   address: '0x53544f434b000000000000000000000000000008' },
  { symbol: 'QQQ',   address: '0x53544f434b000000000000000000000000000009' },
  { symbol: 'GOLD',  address: '0x53544f434b00000000000000000000000000000a' },
]

const CHART_COLORS = [
  '#d4a843', '#4a90e2', '#7ed321', '#e24a4a', '#9b59b6', '#1abc9c',
  '#f39c12', '#2ecc71', '#e74c3c', '#3498db', '#8e44ad', '#16a085',
  '#d35400', '#27ae60', '#c0392b', '#2980b9', '#6c3483', '#148f77',
  '#f1c40f', '#2c3e50', '#7f8c8d', '#e67e22', '#1abc9c', '#e91e63',
  '#ff5722', '#795548', '#607d8b', '#9e9e9e', '#ffc107', '#4caf50',
]

function fmt(n) {
  if (n === undefined || n === null) return ''
  return n >= 1e9 ? `$${(n / 1e9).toFixed(2)}B`
    : n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M`
    : n >= 1e3 ? `$${(n / 1e3).toFixed(2)}K`
    : `$${n.toFixed(2)}`
}

function fmtDate(ts) {
  const d = new Date(typeof ts === 'number' ? ts * 1000 : ts)
  return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:00`
}

function fmtDay(ts) {
  const d = new Date(ts * 1000)
  return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`
}

async function fetchJson(url) {
  try {
    const r = await fetch(url)
    if (!r.ok) return null
    return r.json()
  } catch (_) { return null }
}

// ---------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------

function Trading() {
  const [from, setFrom] = useState(tsToIso(Date.now() - 86400000 * 7))
  const [to, setTo] = useState('')

  const fromTs = useMemo(() => Math.floor(new Date(from).getTime() / 1000), [from])
  const toTs   = useMemo(() => to ? Math.floor(new Date(to).getTime() / 1000) : Math.floor(Date.now() / 1000), [to])

  // -------------------------------------------
  // ASSET PRICES
  // -------------------------------------------
  const [assetChartData, setAssetChartData] = useState({})
  const [pricesLoading, setPricesLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setPricesLoading(true)
    ;(async () => {
      const out = {}
      await Promise.all(TOKENS.map(async tok => {
        const data = await fetchJson(
          `${API_BASE}/api/prices/candles/${tok.address}?resolution=60&from=${fromTs}&to=${toTs}`
        )
        if (!data || !data.length) return
        const prices = data.map(c => c.close || c.high)
        out[tok.symbol] = {
          data: data.map(c => ({ time: c.time, price: c.close || c.high, high: c.high, low: c.low, volume: c.volume })),
          maxPrice: Math.max(...prices),
          minPrice: Math.min(...prices),
        }
      }))
      if (!cancelled) { setAssetChartData(out); setPricesLoading(false) }
    })()
    return () => { cancelled = true }
  }, [fromTs, toTs])

  // -------------------------------------------
  // LIQUIDATIONS
  // -------------------------------------------
  const [liquidations, setLiquidations] = useState([])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const data = await fetchJson(`${API_BASE}/api/stats/liquidations?from=${fromTs}&to=${toTs}`)
      if (!cancelled && data?.data) setLiquidations(data.data)
    })()
    return () => { cancelled = true }
  }, [fromTs, toTs])

  // -------------------------------------------
  // P&L + OI
  // -------------------------------------------
  const [traders, setTraders] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const data = await fetchJson(`${API_BASE}/api/stats/traders?from=${fromTs}`)
      if (!cancelled && data) setTraders(data)
    })()
    return () => { cancelled = true }
  }, [fromTs])

  // -------------------------------------------
  // FEES
  // -------------------------------------------
  const [feesData, setFeesData] = useState([])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const data = await fetchJson(`${API_BASE}/api/stats/fees?from=${fromTs}`)
      if (!cancelled && data?.daily) {
        let cumSwap = 0, cumMargin = 0
        const sorted = [...data.daily].sort((a, b) => a.day_ts - b.day_ts)
        setFeesData(sorted.map(d => {
          cumSwap   += Number(d.swap   || 0) / 1e30
          cumMargin += Number(d.margin || 0) / 1e30
          return {
            timestamp: d.day_ts,
            swap:      cumSwap,
            margin:    cumMargin,
            all:       cumSwap + cumMargin,
          }
        }))
      }
    })()
    return () => { cancelled = true }
  }, [fromTs])

  const pnlData   = traders?.data  || []
  const pnlMax    = pnlData.length ? Math.max(...pnlData.map(p => p.profit || 0)) : 0
  const pnlMin    = pnlData.length ? Math.min(...pnlData.map(p => p.loss   || 0)) : 0
  const oiMax     = pnlData.length ? Math.max(...pnlData.map(p => p.openInterest || 0)) : 0

  // ---------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------
  return (
    <div className="Home">
      <div className="page-title-section">
        <div className="page-title-block">
          <h1>Analytics / Trading — BESC Hyperchain</h1>
          <p className="page-description">Execution-price charts and trading analytics via MoneyX Indexer</p>
        </div>
        <div className="form">
          <label>From&nbsp;</label>
          <input
            type="datetime-local"
            value={from}
            onChange={e => setFrom(e.target.value)}
            style={{ marginRight: '1rem' }}
          />
          <label>To&nbsp;</label>
          <input
            type="datetime-local"
            value={to}
            onChange={e => setTo(e.target.value)}
          />
        </div>
      </div>

      {/* ASSET PRICE CHARTS */}
      <div className="chart-grid">
        {pricesLoading && <div className="chart-cell" style={{ gridColumn: '1/-1', textAlign: 'center', padding: '2rem', color: '#888' }}>Loading price charts…</div>}
        {!pricesLoading && Object.keys(assetChartData).length === 0 && (
          <div className="chart-cell" style={{ gridColumn: '1/-1', textAlign: 'center', padding: '2rem', color: '#888' }}>
            No price data yet — prices populate once trades occur on BESC.
          </div>
        )}
        {Object.entries(assetChartData).map(([sym, { data, maxPrice, minPrice }], i) => (
          <div key={sym} className="chart-cell">
            <h3 style={{ marginBottom: '0.5rem', color: CHART_COLORS[i % CHART_COLORS.length] }}>{sym} / USD</h3>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="time" tickFormatter={fmtDate} minTickGap={40} tick={{ fontSize: 11 }} />
                <YAxis domain={[minPrice * 0.995, maxPrice * 1.005]} tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(1)+'K' : v.toFixed(2)}`} width={65} />
                <Tooltip formatter={(v) => [`$${v.toFixed(4)}`, 'Price']} labelFormatter={fmtDate} />
                <Line type="monotone" dataKey="price" name="Price" dot={false} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ))}

        {/* LIQUIDATED COLLATERAL */}
        {liquidations.length > 0 && (
          <div className="chart-cell">
            <h3 style={{ marginBottom: '0.5rem' }}>Liquidated Collateral (Cumulative)</h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={liquidations}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="timestamp" tickFormatter={fmtDay} minTickGap={40} tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={v => fmt(v)} width={70} />
                <Tooltip formatter={(v, n) => [fmt(v), n]} labelFormatter={fmtDay} />
                <Legend />
                <Area type="monotone" dataKey="longCumulative"  name="Long Liq."  stroke="#7ed321" fill="#7ed32133" strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="shortCumulative" name="Short Liq." stroke="#e24a4a" fill="#e24a4a33" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* P&L */}
        {pnlData.length > 0 && (
          <div className="chart-cell">
            <h3 style={{ marginBottom: '0.5rem' }}>Trader P&L (Daily)</h3>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={pnlData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="timestamp" tickFormatter={fmtDay} minTickGap={40} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="bar"  domain={[pnlMin * 1.1, pnlMax * 1.1]} tickFormatter={fmt} width={70} />
                <YAxis yAxisId="line" orientation="right" domain={[-Math.abs(traders?.stats?.maxAbsCumulativePnl || 0) * 1.1, Math.abs(traders?.stats?.maxAbsCumulativePnl || 0) * 1.1]} tickFormatter={fmt} width={70} tick={{ fill: '#d4a843' }} />
                <Tooltip formatter={(v, n) => [fmt(v), n]} labelFormatter={fmtDay} />
                <Legend />
                <Bar yAxisId="bar" dataKey="profit" name="Profit"          fill="#7ed32166" stackId="pnl" />
                <Bar yAxisId="bar" dataKey="loss"   name="Loss"            fill="#e24a4a66" stackId="pnl" />
                <Line yAxisId="line" type="monotone" dataKey="currentPnlCumulative" name="Cumul. PnL" stroke="#d4a843" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* OPEN INTEREST */}
        {pnlData.length > 0 && (
          <div className="chart-cell">
            <h3 style={{ marginBottom: '0.5rem' }}>Open Interest</h3>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={pnlData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="timestamp" tickFormatter={fmtDay} minTickGap={40} tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={fmt} width={70} domain={[0, oiMax * 1.1]} />
                <Tooltip formatter={(v, n) => [fmt(v), n]} labelFormatter={fmtDay} />
                <Legend />
                <Bar dataKey="longOpenInterest"  name="Long OI"  stackId="oi" fill="#7ed321" />
                <Bar dataKey="shortOpenInterest" name="Short OI" stackId="oi" fill="#e24a4a" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* CUMULATIVE FEES */}
        {feesData.length > 0 && (
          <div className="chart-cell">
            <h3 style={{ marginBottom: '0.5rem' }}>Cumulative Fees</h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={feesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="timestamp" tickFormatter={fmtDay} minTickGap={40} tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={fmt} width={70} />
                <Tooltip formatter={(v, n) => [fmt(v), n]} labelFormatter={fmtDay} />
                <Legend />
                <Area type="monotone" dataKey="swap"   name="Swap Fees"   stroke="#4a90e2" fill="#4a90e222" strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="margin" name="Margin Fees" stroke="#d4a843" fill="#d4a84322" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  )
}

export default Trading
